import { Box, Center, Loader, Stack, Text } from '@mantine/core';
import { IconFile } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
// `?url` emits the worker as a static asset and returns its URL — the heavy
// pdf.js library itself is pulled in lazily below, never in the main bundle.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const THUMB_HEIGHT = 130;

export type DocThumbItem = {
  mimeType: string | null;
  mediaUrl: string | null;
  fileName: string | null;
};

/** Load pdf.js once, on first use, and wire its worker to the Vite-emitted
 *  asset URL (a CDN workerSrc would risk a version mismatch). */
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null;
function getPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

function extLabel(item: DocThumbItem): string {
  return (item.fileName?.split('.').pop() || item.mimeType?.split('/').pop() || 'FILE')
    .toUpperCase()
    .slice(0, 4);
}

function IconTile({ label }: { label: string }) {
  return (
    <Stack
      align="center"
      justify="center"
      gap={4}
      style={{
        height: THUMB_HEIGHT,
        background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
      }}
    >
      <IconFile size={36} opacity={0.55} />
      <Text size="xs" c="dimmed" fw={600}>
        {label}
      </Text>
    </Stack>
  );
}

/** First-page preview for PDF documents, rendered client-side with pdf.js the
 *  moment the tile scrolls into view. Non-PDF documents (or any load/render
 *  failure) fall back to the same labeled icon tile the rest of the grid uses. */
export function DocThumb({ item }: { item: DocThumbItem }) {
  const isPdf = item.mimeType === 'application/pdf';
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [visible, setVisible] = useState(false);

  // Defer the (heavy) render until the tile is near/in the viewport.
  useEffect(() => {
    if (!isPdf || visible) return;
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isPdf, visible]);

  useEffect(() => {
    const url = item.mediaUrl;
    if (!isPdf || !visible || !url) return;
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    setStatus('loading');
    void (async () => {
      try {
        const pdfjs = await getPdfjs();
        const task = pdfjs.getDocument({ url, withCredentials: true });
        const pdf = await task.promise;
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) {
          void task.destroy();
          return;
        }
        const cssWidth = wrapRef.current?.clientWidth || 200;
        const dpr = window.devicePixelRatio || 1;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: (cssWidth * dpr) / base.width });
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const renderTask = page.render({ canvas, viewport });
        cleanup = () => {
          renderTask.cancel();
          void task.destroy();
        };
        await renderTask.promise;
        if (!cancelled) setStatus('done');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [isPdf, visible, item.mediaUrl]);

  if (!isPdf || status === 'error') return <IconTile label={extLabel(item)} />;

  // Canvas is anchored to the top of a fixed-height window so the first page
  // reads like a document cover rather than a squashed full page.
  return (
    <Box
      ref={wrapRef}
      style={{
        position: 'relative',
        height: THUMB_HEIGHT,
        overflow: 'hidden',
        background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%' }} />
      {status !== 'done' && (
        <Center style={{ position: 'absolute', inset: 0 }}>
          <Loader size="sm" />
        </Center>
      )}
    </Box>
  );
}
